import React from "react";
import { Check, ArrowRight, Car } from "lucide-react";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const wp_query_params = {
  per_page: 3,
  order: "asc",
  orderby: "price",
  status: "publish",
  post_type: "product"
};
const wp_query = new WP_Query(wp_query_params);
function LeasePackagesList() {
  const {
    products,
    loading,
    error,
    refetch
  } = useEcommerceProducts();
  if (loading) {
    return <div className="space-y-6">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="w-full h-64 rounded-xl bg-muted/10 animate-pulse border border-border/40" />)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-12 text-muted-foreground">
        <p>Unable to load lease packages.</p>
        <Button onClick={refetch} variant="link" className="mt-2 text-primary">
          Try Again
        </Button>
      </div>;
  }
  if (products.length === 0) {
    return <div className="text-center py-12 text-muted-foreground">
        <p>No lease packages currently available.</p>
      </div>;
  }
  return <div className="space-y-6">
      {products.map((product, i) => <Card key={product.id} data-index={i} className="overflow-hidden border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/50 transition-colors duration-300">
          <div className="flex flex-col md:flex-row">
            {}
            <div className="md:w-1/3 p-8 flex flex-col justify-center border-b md:border-b-0 md:border-r border-border/50 bg-muted/5">
              <div className="mb-4">
                <h3 className="text-2xl font-bold tracking-tight text-foreground">{product.title}</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {i === 0 ? "Perfect for individuals" : i === 1 ? "Best for small businesses" : "Corporate fleet solution"}
                </p>
              </div>
              
              <div className="mt-auto">
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-primary">
                    £{product.price}
                  </span>
                  <span className="text-muted-foreground font-medium">/ month</span>
                </div>
                {product.customFields?.billing_cycle && <p className="text-xs text-muted-foreground mt-1">
                    Billed {product.customFields.billing_cycle}
                  </p>}
              </div>
            </div>

            {}
            <div className="md:w-2/3 p-8 flex flex-col justify-between gap-6">
              <div>
                <h4 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4 flex items-center gap-2">
                  <Car className="w-4 h-4" /> Package Features
                </h4>
                
                {}
                {product.short_description ? <div className="prose prose-invert prose-sm max-w-none 
                      [&_ul]:grid [&_ul]:grid-cols-1 [&_ul]:sm:grid-cols-2 [&_ul]:gap-x-8 [&_ul]:gap-y-3 [&_ul]:list-none [&_ul]:p-0 
                      [&_li]:flex [&_li]:items-start [&_li]:gap-2 [&_li]:m-0
                      [&_li]:before:content-[''] [&_li]:before:text-primary [&_li]:before:font-bold" dangerouslySetInnerHTML={{
              __html: product.short_description
            }} /> : <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                    {}
                    {["Comprehensive Insurance", "24/7 Roadside Assistance", "Routine Maintenance", "Replacement Vehicle", "Mileage Allowance: 1500km", "Flexible Contract Terms"].map((feature, idx) => <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>)}
                  </ul>}
              </div>

              <div className="flex flex-col sm:flex-row items-center gap-4 pt-4 border-t border-border/30 mt-auto">
                <div className="w-full sm:w-auto flex-1">
                   <AddToCartButton productId={product.id} variant="default" size="lg" text="Request Quote" className="w-full" buttonClassName="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold shadow-lg shadow-primary/20" showQuantity={false} />
                </div>
                <Link to={product.link} className="text-sm font-medium text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors">
                  View Details <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </Card>)}
    </div>;
}
export default function LeasePackages() {
  return <section id="lease-packages" className="dark bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-7xl pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-600/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 tracking-tight">
            Flexible Lease Packages
          </h2>
          <p className="text-lg text-muted-foreground">
            Choose the perfect leasing tier for your needs. From individual mobility to comprehensive corporate fleet management, we have a solution that drives you forward.
          </p>
        </div>

        <EcommerceProductsProvider wp_query={{
        per_page: 3,
        order: "asc",
        orderby: "price",
        status: ["publish"],
        post_type: "product"
      }} className="w-full max-w-5xl mx-auto">
          <LeasePackagesList />
        </EcommerceProductsProvider>
      </div>
    </section>;
}